import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CollapsibleNote } from '../CollapsibleNote';
import { useWorkflowOverview } from '../../../hooks/useWorkflowOverview';
import { useReportTimeline } from '../../../hooks/useReportTimeline';
import { apiClient } from '../../../services/apiClient';
import type { Report } from '../../../types';
import './WorkflowTab.css';

interface WorkflowTabProps {
  year: number;
}

const ORIGIN_LABEL_KEYS: Record<string, string> = {
  creation: 'analytics.workflow.origin.creation',
  status_endpoint: 'analytics.workflow.origin.statusEndpoint',
  generic_update: 'analytics.workflow.origin.genericUpdate',
  backfill: 'analytics.workflow.origin.backfill',
};

// Ordine di lifecycle del report, non alfabetico. CLOSED e' escluso a valle (stato finale, la
// permanenza non e' significativa se non per le rare riaperture, che scegliamo di ignorare qui).
const LIFECYCLE_STATUS_ORDER = ['NEW', 'READY', 'IN_PROGRESS', 'SIGNED'];

export const WorkflowTab: React.FC<WorkflowTabProps> = ({ year }) => {
  const { t, i18n } = useTranslation();
  const { overview, loading, error } = useWorkflowOverview(year);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Report[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);

  const { timeline, loading: loadingTimeline, error: timelineError } = useReportTimeline(selectedReport?.id ?? null);

  const numberFormatter = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);
  const hoursFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    [i18n.language]
  );
  const dateTimeFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }),
    [i18n.language]
  );

  const timeInState = useMemo(() => {
    if (!overview) return [];
    return overview.timeInState
      .filter((row) => row.status !== 'CLOSED')
      .slice()
      .sort((a, b) => {
        const indexA = LIFECYCLE_STATUS_ORDER.indexOf(a.status);
        const indexB = LIFECYCLE_STATUS_ORDER.indexOf(b.status);
        if (indexA === -1 && indexB === -1) return a.status.localeCompare(b.status);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
  }, [overview]);

  const maxStateHours = useMemo(
    () => Math.max(1, ...timeInState.map((s) => s.avgHours)),
    [timeInState]
  );

  const handleSearch = async (value: string) => {
    setQuery(value);
    if (value.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const found = await apiClient.searchReports(value.trim());
      setResults(found.slice(0, 8));
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const selectReport = (report: Report) => {
    setSelectedReport(report);
    setResults([]);
    setQuery(report.number);
  };

  return (
    <div className="analytics-workflow">
      <CollapsibleNote variant="warn" title={t('analytics.workflow.dataQualityTitle')}>
        {t('analytics.workflow.dataQualityBanner')}
      </CollapsibleNote>

      {loading && <p className="analytics-coming-soon">{t('app.loading')}</p>}
      {!loading && (error || !overview) && <p className="analytics-error">{t('analytics.loadError')}</p>}

      {!loading && overview && (
        <>
          <div className="analytics-kpi-cards">
            <div className="analytics-kpi-card">
              <div className="analytics-kpi-label">{t('analytics.workflow.leadTimeLabel')}</div>
              <div className="analytics-kpi-value">
                {overview.leadTimeNewToClosed.avgHours !== null
                  ? `${hoursFormatter.format(overview.leadTimeNewToClosed.avgHours / 24)} ${t('analytics.workflow.days')}`
                  : t('analytics.notAvailable')}
              </div>
              <div className="analytics-workflow-kpi-caption">
                {t('analytics.workflow.leadTimeSampleSize', { count: overview.leadTimeNewToClosed.sampleSize })}
              </div>
            </div>
          </div>

          <div className="analytics-panel">
            <h3>{t('analytics.workflow.timeInStateTitle')}</h3>
            {timeInState.length === 0 && (
              <p className="analytics-coming-soon">{t('analytics.workflow.noTimeInState')}</p>
            )}
            {timeInState.length > 0 && (
              <div className="analytics-hbar-list">
                {timeInState.map((row) => (
                  <div className="analytics-hbar-row analytics-workflow-hbar-row" key={row.status}>
                    <span className="analytics-hbar-label">{row.status}</span>
                    <div className="analytics-hbar-track">
                      <div
                        className="analytics-hbar-fill"
                        style={{ width: `${(row.avgHours / maxStateHours) * 100}%` }}
                      />
                    </div>
                    <div className="analytics-workflow-hbar-meta">
                      <span className="analytics-workflow-hbar-value">
                        {hoursFormatter.format(row.avgHours)} {t('analytics.workflow.hoursShort')}
                      </span>
                      <span className="analytics-workflow-hbar-sub">
                        {t('analytics.workflow.occurrences', { count: row.occurrences })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="analytics-panel">
            <h3>{t('analytics.workflow.pathVariantsTitle')}</h3>
            {overview.pathVariants.length === 0 && (
              <p className="analytics-coming-soon">{t('analytics.workflow.noPathVariants')}</p>
            )}
            {overview.pathVariants.length > 0 && (
              <div className="analytics-tech-table-container">
                <table className="analytics-tech-table">
                  <thead>
                    <tr>
                      <th>{t('analytics.workflow.columns.path')}</th>
                      <th className="n">{t('analytics.workflow.columns.reportCount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.pathVariants.map((row) => (
                      <tr key={row.path}>
                        <td className="analytics-workflow-path-cell">{row.path}</td>
                        <td className="n">{numberFormatter.format(row.reportCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </>
      )}

      <div className="analytics-panel">
        <h3>{t('analytics.workflow.reportTimelineTitle')}</h3>
        <div className="analytics-workflow-search">
          <input
            type="text"
            className="analytics-workflow-search-input"
            placeholder={t('analytics.workflow.searchPlaceholder')}
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {searching && <span className="analytics-workflow-search-status">{t('app.loading')}</span>}
          {results.length > 0 && (
            <ul className="analytics-workflow-search-results">
              {results.map((r) => (
                <li key={r.id}>
                  <button type="button" onClick={() => selectReport(r)}>
                    {r.number} — {r.customerName ?? t('analytics.notAvailable')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {selectedReport && loadingTimeline && <p className="analytics-coming-soon">{t('app.loading')}</p>}
        {selectedReport && !loadingTimeline && (timelineError || !timeline) && (
          <p className="analytics-error">{t('analytics.loadError')}</p>
        )}
        {selectedReport && !loadingTimeline && timeline && (
          <div className="analytics-workflow-timeline">
            <div className="analytics-workflow-timeline-header">
              <strong>{timeline.reportNumber}</strong>
              {timeline.totalLeadTimeHours !== null && (
                <span>
                  {t('analytics.workflow.totalLeadTime')}:{' '}
                  {hoursFormatter.format(timeline.totalLeadTimeHours / 24)} {t('analytics.workflow.days')}
                </span>
              )}
            </div>
            <ol className="analytics-workflow-timeline-list">
              {timeline.events.map((ev, idx) => (
                <li key={idx}>
                  <span className="analytics-workflow-timeline-status">
                    {ev.fromStatus ? `${ev.fromStatus} → ${ev.toStatus}` : ev.toStatus}
                  </span>
                  <span className="analytics-workflow-timeline-date">{dateTimeFormatter.format(new Date(ev.occurredAt))}</span>
                  <span className="analytics-workflow-timeline-origin">
                    {t(ORIGIN_LABEL_KEYS[ev.origin] ?? 'analytics.workflow.origin.genericUpdate')}
                  </span>
                  {ev.hoursSincePrevious !== null && (
                    <span className="analytics-workflow-timeline-delta">
                      +{hoursFormatter.format(ev.hoursSincePrevious)} {t('analytics.workflow.hoursShort')}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowTab;
